import React, { useState, useEffect, useCallback } from 'react';
import { DollarSign, CheckCircle, RefreshCw, ChevronDown, ChevronUp, Banknote } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { staffAPI } from '../../services/api';
import { format } from 'date-fns';
import StaffTabs from './StaffTabs';
import { Badge, Card, EmptyState, Button, Modal, FormField, InfoBanner, Spinner } from '../../components/ui';

function InlineEdit({ value, onSave, label, prefix = '₹' }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  const commit = () => {
    setEditing(false);
    const num = Number(draft) || 0;
    if (num !== Number(value)) onSave(num);
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => { setDraft(value); setEditing(true); }}
        className="text-left hover:bg-ga-bg-surface-subtle px-2 py-1 -mx-2 -my-1 rounded-ga-sm transition-colors w-full text-ga-text-primary"
        title="Click to edit"
      >
        {prefix}{Number(value || 0).toLocaleString()}
      </button>
    );
  }
  return (
    <input
      type="number"
      aria-label={label}
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
      className="ga-input w-24 px-2 py-1 rounded-ga-sm text-sm"
    />
  );
}

export default function SalaryManagement() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [list, setList] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingId, setMarkingId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [showAdvanceModal, setShowAdvanceModal] = useState(false);
  const [showAdvances, setShowAdvances] = useState(false);

  const fetchData = useCallback(() => {
    setLoading(true);
    Promise.all([
      staffAPI.getSalaryByPeriod(month, year),
      staffAPI.getAdvances({ month, year }),
      staffAPI.list({ status: 'ACTIVE' }),
    ]).then(([salRes, advRes, staffRes]) => {
      setList(salRes.data || []);
      setAdvances(advRes.data || []);
      setStaffList(staffRes.data || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, [month, year]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleMarkPaid = async (salaryId) => {
    setMarkingId(salaryId);
    try {
      await staffAPI.markSalaryPaid(salaryId);
      fetchData();
    } catch (err) { alert(err.message || 'Failed'); }
    finally { setMarkingId(null); }
  };

  const handleMarkAllPaid = async () => {
    const pending = list.filter(r => r.payment_status === 'PENDING');
    if (pending.length === 0) return;
    if (!window.confirm(`Mark all ${pending.length} pending salaries as paid?`)) return;
    setMarkingAll(true);
    try {
      for (const r of pending) await staffAPI.markSalaryPaid(r.id);
      fetchData();
    } catch (err) { alert(err.message || 'Failed'); }
    finally { setMarkingAll(false); }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await staffAPI.generateSalaries(month, year);
      if (res.success) fetchData();
      else alert(res.message || 'Failed to generate');
    } catch (err) { alert(err.message || 'Failed'); }
    finally { setGenerating(false); }
  };

  const handleInlineUpdate = async (row, field, value) => {
    try {
      await staffAPI.addSalary({
        staff_id: row.staff_id,
        salary_month: row.salary_month,
        salary_year: row.salary_year,
        base_salary: field === 'base_salary' ? value : row.base_salary,
        extra_pay: field === 'extra_pay' ? value : row.extra_pay,
        deductions: field === 'deductions' ? value : row.deductions,
        payment_status: row.payment_status,
        payment_date: row.payment_date,
      });
      fetchData();
    } catch (err) { alert(err.message || 'Save failed'); }
  };

  const handleRecoverAdvance = async (advId) => {
    if (!window.confirm('Mark this advance as recovered?')) return;
    try {
      await staffAPI.recoverAdvance(advId, null);
      fetchData();
    } catch (err) { alert(err.message || 'Failed'); }
  };

  // KPIs
  const totalPayroll = list.reduce((s, r) => s + Number(r.final_salary || 0), 0);
  const totalPaid = list.filter(r => r.payment_status === 'PAID').reduce((s, r) => s + Number(r.final_salary || 0), 0);
  const totalPending = list.filter(r => r.payment_status === 'PENDING').reduce((s, r) => s + Number(r.final_salary || 0), 0);
  const totalAdvance = advances.filter(a => a.status === 'GIVEN').reduce((s, a) => s + Number(a.amount || 0), 0);
  const pendingCount = list.filter(r => r.payment_status === 'PENDING').length;
  const outstandingAdvances = advances.filter(a => a.status === 'GIVEN').length;

  // Table totals
  const totBase = list.reduce((s, r) => s + Number(r.base_salary || 0), 0);
  const totExtra = list.reduce((s, r) => s + Number(r.extra_pay || 0), 0);
  const totDed = list.reduce((s, r) => s + Number(r.deductions || 0), 0);

  const monthName = format(new Date(year, month - 1, 1), 'MMMM yyyy');

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-7xl mx-auto">
        <StaffTabs active="salary" />

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-ga-text-primary flex items-center gap-2">
              <DollarSign className="h-7 w-7 text-ga-primary" />
              Salary Management
            </h1>
            <p className="text-ga-text-muted mt-1">{monthName}</p>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <label htmlFor="salary-month" className="sr-only">Month</label>
            <select id="salary-month" value={month} onChange={(e) => setMonth(parseInt(e.target.value, 10))} className="ga-input w-32 py-2 rounded-ga-md text-sm">
              {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
                <option key={m} value={m}>{format(new Date(2000, m - 1, 1), 'MMMM')}</option>
              ))}
            </select>
            <label htmlFor="salary-year" className="sr-only">Year</label>
            <select id="salary-year" value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} className="ga-input w-24 py-2 rounded-ga-md text-sm">
              {[year + 1, year, year - 1, year - 2].map((y) => (<option key={y} value={y}>{y}</option>))}
            </select>
            <Button onClick={handleGenerate} disabled={generating} className="text-sm">
              <RefreshCw className={`h-4 w-4 ${generating ? 'animate-spin' : ''}`} />
              {generating ? 'Generating...' : 'Generate Salaries'}
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card padding="sm">
            <p className="text-xs text-ga-text-subtle mb-1">Total Payroll</p>
            <p className="text-2xl font-bold text-ga-text-primary">₹{totalPayroll.toLocaleString()}</p>
            <p className="text-xs text-ga-text-subtle mt-1">{list.length} staff</p>
          </Card>
          <Card padding="sm">
            <p className="text-xs text-ga-text-subtle mb-1">Paid</p>
            <p className="text-2xl font-bold text-ga-success">₹{totalPaid.toLocaleString()}</p>
            <p className="text-xs text-ga-success mt-1">{list.filter(r => r.payment_status === 'PAID').length} paid</p>
          </Card>
          <Card padding="sm">
            <p className="text-xs text-ga-text-subtle mb-1">Pending</p>
            <p className="text-2xl font-bold text-ga-warning">₹{totalPending.toLocaleString()}</p>
            <p className="text-xs text-ga-warning mt-1">{pendingCount} pending</p>
          </Card>
          <Card padding="sm">
            <p className="text-xs text-ga-text-subtle mb-1">Advance Given</p>
            <p className="text-2xl font-bold text-ga-info">₹{totalAdvance.toLocaleString()}</p>
            <p className="text-xs text-ga-info mt-1">{outstandingAdvances} outstanding</p>
          </Card>
        </div>

        {/* Actions bar */}
        {list.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {pendingCount > 0 && (
              <button
                type="button"
                onClick={handleMarkAllPaid}
                disabled={markingAll}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-ga-success text-ga-text-on-primary rounded-ga-md text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
              >
                <CheckCircle className="h-4 w-4" />
                {markingAll ? 'Processing...' : `Mark All Paid (${pendingCount})`}
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowAdvanceModal(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-ga-info text-ga-text-on-primary rounded-ga-md text-sm font-medium hover:opacity-90 transition-opacity"
            >
              <Banknote className="h-4 w-4" />
              Give Advance
            </button>
          </div>
        )}

        {/* Salary Table */}
        <Card padding="none" className="overflow-hidden">
          {loading ? (
            <div className="py-12 flex justify-center">
              <Spinner />
            </div>
          ) : list.length === 0 ? (
            <EmptyState
              icon={DollarSign}
              title="No Salary Records"
              description={`No salary records for ${monthName}.`}
              action={(
                <Button onClick={handleGenerate} disabled={generating}>
                  <RefreshCw className={`h-4 w-4 ${generating ? 'animate-spin' : ''}`} />
                  Generate Salaries for All Active Staff
                </Button>
              )}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-ga-bg-surface-subtle border-b border-ga-border-subtle text-left text-ga-text-muted">
                    <th className="p-3 font-semibold">Staff</th>
                    <th className="p-3 font-semibold">Role</th>
                    <th className="p-3 font-semibold text-right">Base Salary</th>
                    <th className="p-3 font-semibold text-right">Extra Pay</th>
                    <th className="p-3 font-semibold text-right">Deductions</th>
                    <th className="p-3 font-semibold text-right">Final Salary</th>
                    <th className="p-3 font-semibold text-center">Status</th>
                    <th className="p-3 font-semibold text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((row) => {
                    const isPaid = row.payment_status === 'PAID';
                    return (
                      <tr key={row.id} className={`border-b border-ga-border-subtle/50 transition-colors hover:bg-ga-bg-surface-subtle/50 ${isPaid ? 'border-l-[3px] border-l-ga-success' : 'border-l-[3px] border-l-ga-warning'}`}>
                        <td className="p-3 font-medium text-ga-text-primary">{row.staff_name}</td>
                        <td className="p-3 text-ga-text-muted capitalize">{row.staff_role?.toLowerCase()}</td>
                        <td className="p-3 text-right">
                          <InlineEdit label={`Base salary for ${row.staff_name}`} value={row.base_salary} onSave={(v) => handleInlineUpdate(row, 'base_salary', v)} />
                        </td>
                        <td className="p-3 text-right">
                          <InlineEdit label={`Extra pay for ${row.staff_name}`} value={row.extra_pay} onSave={(v) => handleInlineUpdate(row, 'extra_pay', v)} />
                        </td>
                        <td className="p-3 text-right">
                          <InlineEdit label={`Deductions for ${row.staff_name}`} value={row.deductions} onSave={(v) => handleInlineUpdate(row, 'deductions', v)} />
                        </td>
                        <td className="p-3 text-right font-semibold text-ga-text-primary">₹{Number(row.final_salary).toLocaleString()}</td>
                        <td className="p-3 text-center">
                          <Badge variant={isPaid ? 'success' : 'warning'}>{row.payment_status}</Badge>
                          {isPaid && row.payment_date && (
                            <p className="text-[10px] text-ga-text-subtle mt-0.5">
                              {format(new Date(row.payment_date), 'dd MMM')}
                            </p>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          {!isPaid && (
                            <button
                              type="button"
                              onClick={() => handleMarkPaid(row.id)}
                              disabled={markingId === row.id}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-ga-success text-ga-text-on-primary rounded-ga-md text-xs font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
                            >
                              <CheckCircle className="h-3.5 w-3.5" />
                              {markingId === row.id ? '...' : 'Pay'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-ga-bg-surface-subtle border-t-2 border-ga-border-subtle font-semibold text-ga-text-primary">
                    <td className="p-3" colSpan={2}>Total</td>
                    <td className="p-3 text-right">₹{totBase.toLocaleString()}</td>
                    <td className="p-3 text-right">₹{totExtra.toLocaleString()}</td>
                    <td className="p-3 text-right">₹{totDed.toLocaleString()}</td>
                    <td className="p-3 text-right">₹{totalPayroll.toLocaleString()}</td>
                    <td className="p-3" colSpan={2}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </Card>

        {/* Advances Section */}
        <Card padding="none" className="overflow-hidden">
          <button
            type="button"
            onClick={() => setShowAdvances(!showAdvances)}
            className="w-full flex items-center justify-between p-4 hover:bg-ga-bg-surface-subtle transition-colors"
          >
            <h2 className="font-semibold text-ga-text-primary flex items-center gap-2">
              <Banknote className="h-5 w-5 text-ga-info" />
              Salary Advances
              {outstandingAdvances > 0 && (
                <Badge variant="info">{outstandingAdvances} outstanding</Badge>
              )}
            </h2>
            {showAdvances ? <ChevronUp className="h-5 w-5 text-ga-text-muted" /> : <ChevronDown className="h-5 w-5 text-ga-text-muted" />}
          </button>
          {showAdvances && (
            <div className="border-t border-ga-border-subtle">
              {advances.length === 0 ? (
                <div className="py-8 text-center text-ga-text-muted text-sm">No advance records for this period.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-ga-bg-surface-subtle border-b border-ga-border-subtle text-left text-ga-text-muted">
                      <th className="p-3 font-semibold">Staff</th>
                      <th className="p-3 font-semibold">Date</th>
                      <th className="p-3 font-semibold text-right">Amount</th>
                      <th className="p-3 font-semibold">Reason</th>
                      <th className="p-3 font-semibold text-center">Status</th>
                      <th className="p-3 font-semibold text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {advances.map((a) => (
                      <tr key={a.id} className="border-b border-ga-border-subtle/50 hover:bg-ga-bg-surface-subtle/50">
                        <td className="p-3 font-medium text-ga-text-primary">{a.staff_name}</td>
                        <td className="p-3 text-ga-text-muted">{a.advance_date ? format(new Date(a.advance_date), 'dd MMM yyyy') : '-'}</td>
                        <td className="p-3 text-right font-medium text-ga-text-primary">₹{Number(a.amount).toLocaleString()}</td>
                        <td className="p-3 text-ga-text-muted">{a.reason || '-'}</td>
                        <td className="p-3 text-center">
                          <Badge variant={a.status === 'GIVEN' ? 'info' : 'neutral'}>{a.status}</Badge>
                        </td>
                        <td className="p-3 text-center">
                          {a.status === 'GIVEN' && (
                            <button type="button" onClick={() => handleRecoverAdvance(a.id)} className="text-xs text-ga-info hover:underline font-medium">
                              Recover
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </Card>
      </div>

      {/* Give Advance Modal */}
      <AdvanceModal
        open={showAdvanceModal}
        staffList={staffList}
        onClose={() => setShowAdvanceModal(false)}
        onSaved={() => { setShowAdvanceModal(false); setShowAdvances(true); fetchData(); }}
      />
    </DashboardLayout>
  );
}

function AdvanceModal({ open, staffList, onClose, onSaved }) {
  const [form, setForm] = useState({
    staff_id: '',
    amount: '',
    reason: '',
    advance_date: new Date().toISOString().slice(0, 10),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.staff_id) { setError('Select a staff member'); return; }
    if (!form.amount || Number(form.amount) <= 0) { setError('Enter a valid amount'); return; }
    setSaving(true);
    setError('');
    try {
      const res = await staffAPI.addAdvance({ ...form, amount: Number(form.amount) });
      if (res.success) onSaved();
      else setError(res.message || 'Failed');
    } catch (err) { setError(err.message || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title="Give Advance Salary"
      footer={(
        <div className="flex gap-3">
          <Button type="submit" form="advance-form" disabled={saving} className="flex-1">
            {saving ? 'Processing...' : 'Give Advance'}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
        </div>
      )}
    >
      <form id="advance-form" onSubmit={handleSubmit} className="space-y-4">
        {error && <InfoBanner variant="danger">{error}</InfoBanner>}
        <FormField label="Staff Member" htmlFor="advance-staff" required>
          <select id="advance-staff" value={form.staff_id} onChange={(e) => setForm(f => ({ ...f, staff_id: e.target.value }))} className="ga-input w-full rounded-ga-md">
            <option value="">Select staff...</option>
            {staffList.map(s => (
              <option key={s.id} value={s.id}>{s.name} — {s.role}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Amount (₹)" htmlFor="advance-amount" required>
          <input id="advance-amount" type="number" min="1" step="0.01" value={form.amount} onChange={(e) => setForm(f => ({ ...f, amount: e.target.value }))} className="ga-input w-full rounded-ga-md" placeholder="e.g. 5000" />
        </FormField>
        <FormField label="Date" htmlFor="advance-date">
          <input id="advance-date" type="date" value={form.advance_date} onChange={(e) => setForm(f => ({ ...f, advance_date: e.target.value }))} className="ga-input w-full rounded-ga-md" />
        </FormField>
        <FormField label="Reason" htmlFor="advance-reason">
          <textarea id="advance-reason" value={form.reason} onChange={(e) => setForm(f => ({ ...f, reason: e.target.value }))} rows={2} className="ga-input w-full rounded-ga-md" placeholder="Optional reason for advance" />
        </FormField>
      </form>
    </Modal>
  );
}
